export type BulkUploadScenario = 'errors' | 'clean';

export type ValidationIssue = {
    row: number;
    title: string;
    detail: string;
};

export type ExistingUserUpdate = {
    email: string;
    currentRole: string;
    newRole: string;
    currentAssignments: string;
    newAssignments: string;
};

export type NewUserPreview = {
    name: string;
    email: string;
    role: string;
    group: string;
    team: string;
    sendInvite: string;
};

export type StructurePreview = {
    group: string;
    teams: string;
    users: string;
};

export type ScenarioData = {
    fileName: string;
    rowsDetected: number;
    issues: ValidationIssue[];
    updates: ExistingUserUpdate[];
    newUsers: NewUserPreview[];
    summary: {
        usersCreated: number;
        usersUpdated: number;
        groupsCreated: number;
        teamsCreated: number;
        invitesSent: number;
    };
    highlights: string[];
    structure: StructurePreview[];
};

export const BULK_UPLOAD_TEMPLATE_PATH = '/static/templates/system-user-bulk-upload-template.xlsx';

export const BULK_UPLOAD_SCENARIOS: Record<BulkUploadScenario, ScenarioData> = {
    errors: {
        fileName: 'system-user-upload.xlsx',
        rowsDetected: 8,
        issues: [
            {
                row: 3,
                title: 'Team assignment requires parent Group to be specified',
                detail: 'john.smith@example.com includes Team "Team B" without a Group value.',
            },
            {
                row: 5,
                title: 'Conflicting data for sarah.jones@example.com',
                detail: 'Role differs across repeated rows: "Admin" versus "Team Leader".',
            },
            {
                row: 7,
                title: 'Invalid Alert Notification Type',
                detail: '"Push" is not valid. Allowed values are SMS, Email, Phone, Browser Notification, or None.',
            },
        ],
        updates: [
            {
                email: 'jane.doe@example.com',
                currentRole: 'Team Leader',
                newRole: 'Admin',
                currentAssignments: 'Northeast > Team A',
                newAssignments: 'Northeast > Team A, Northeast > Team C',
            },
            {
                email: 'bob.smith@example.com',
                currentRole: 'Group Leader',
                newRole: 'Group Leader',
                currentAssignments: 'West Region',
                newAssignments: 'West Region, Southeast',
            },
        ],
        newUsers: [
            {
                name: 'John Smith',
                email: 'john.smith@example.com',
                role: 'Team Leader',
                group: 'Northeast',
                team: 'Team A, Team B',
                sendInvite: 'Yes',
            },
            {
                name: 'Sarah Jones',
                email: 'sarah.jones@example.com',
                role: 'Group Leader',
                group: 'Southeast',
                team: 'No direct team assignment',
                sendInvite: 'Yes',
            },
            {
                name: 'Mike Wilson',
                email: 'mike.wilson@example.com',
                role: 'Team Leader',
                group: 'Southeast',
                team: 'Skilled',
                sendInvite: 'No',
            },
        ],
        summary: {
            usersCreated: 3,
            usersUpdated: 2,
            groupsCreated: 2,
            teamsCreated: 4,
            invitesSent: 3,
        },
        highlights: [
            '3 blocking validation issues must be resolved before upload can continue.',
            '2 existing users would be updated after confirmation.',
            '2 new groups and 4 new teams are waiting to be created.',
        ],
        structure: [
            {
                group: 'Northeast',
                teams: 'Team A, Team B, Team C',
                users: 'John Smith, Jane Doe',
            },
            {
                group: 'Southeast',
                teams: 'Skilled',
                users: 'Sarah Jones, Mike Wilson',
            },
        ],
    },
    clean: {
        fileName: 'system-user-upload-ready.xlsx',
        rowsDetected: 5,
        issues: [],
        updates: [
            {
                email: 'jane.doe@example.com',
                currentRole: 'Team Leader',
                newRole: 'Admin',
                currentAssignments: 'Northeast > Team A',
                newAssignments: 'Northeast > Team A, Northeast > Team C',
            },
        ],
        newUsers: [
            {
                name: 'John Smith',
                email: 'john.smith@example.com',
                role: 'Team Leader',
                group: 'Northeast',
                team: 'Team A, Team B',
                sendInvite: 'Yes',
            },
            {
                name: 'Sarah Jones',
                email: 'sarah.jones@example.com',
                role: 'Group Leader',
                group: 'Southeast',
                team: 'No direct team assignment',
                sendInvite: 'Yes',
            },
            {
                name: 'Mike Wilson',
                email: 'mike.wilson@example.com',
                role: 'Team Leader',
                group: 'Southeast',
                team: 'Skilled',
                sendInvite: 'No',
            },
        ],
        summary: {
            usersCreated: 3,
            usersUpdated: 1,
            groupsCreated: 2,
            teamsCreated: 4,
            invitesSent: 2,
        },
        highlights: [
            'All validations passed for this workbook.',
            '1 existing user will be updated after confirmation.',
            'Only newly created users with Send Invite set to Yes will receive invites.',
        ],
        structure: [
            {
                group: 'Northeast',
                teams: 'Team A, Team B, Team C',
                users: 'John Smith (Team A, Team B), Jane Doe (Team A, Team C)',
            },
            {
                group: 'Southeast',
                teams: 'Skilled',
                users: 'Sarah Jones (Group Leader), Mike Wilson (Team Skilled)',
            },
        ],
    },
};
